import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSim } from "../state/store";
import { RiskMeterList } from "./RiskMeter";
import { ResultsCompact } from "./ResultsCompact";
import {
  apiUrl,
  checkQuizAnswer,
  getHostConfig,
  hostBody,
  hostPostHeaders,
  isSpokeLoad,
  readCsrfCookie,
  type HostCheck,
} from "../lib/hostConfig";
import { newShuffleSeed, shuffleOptionSets } from "../lib/shuffle";
import type { QuizQuestion } from "../scenario/types";
import { tx, txr } from "../lib/tx";

/** The host served the spec without quiz answer keys (`hostGraded: true`, /sim/spec with SIM_SPEC_STRIP_KEYS):
 *  every answer is graded by POST /t/<token>/quiz-check instead of `options[].correct`. */
function isHostGraded(scenario: unknown): boolean {
  return getHostConfig().hosted && (scenario as { hostGraded?: unknown } | null)?.hostGraded === true;
}

/**
 * Host-mode reporting (Cyberfrog only). Posts the quiz to /t/<token>/quiz so
 * grade_and_complete sets training_assignments.quiz_score/quiz_passed/status
 * (exactly like every HTML e-learning), and the behavioural outcomes + score to
 * /sim/track for journey_outcomes. A course without a quiz then posts an empty
 * /quiz: the server completes it once it saw this debrief post and credited 60 s
 * of heartbeats (else a soft 409 not_yet_counted; a later heartbeat completes it).
 * Best-effort: failures never block the learner.
 */
async function reportToHost(answers: Record<string, string>): Promise<void> {
  const host = getHostConfig();
  if (!host.hosted || !host.token) return;
  // A hub exercise page (?m=<spoke>): the hub's own quiz is the graded one.
  if (isSpokeLoad()) return;
  const st = useSim.getState();
  const scn = st.scenario;
  if (!scn) return;
  const quiz: QuizQuestion[] = scn.beats.find((b) => b.type === "quiz")?.quiz ?? [];
  const csrf = readCsrfCookie();
  const postQuiz = async (submissions: { question_position: number; selected_choices: string[] }[]) => {
    try {
      await fetch(apiUrl(`/t/${host.token}/quiz`), {
        method: "POST",
        headers: hostPostHeaders(),
        credentials: "include",
        body: hostBody({ submissions }),
      });
    } catch {
      /* best-effort */
    }
  };

  // Quiz: question_position is the raw index in the quiz beat; selected_choices
  // are the option *ids* (the synced quiz_questions store each option id as its
  // choice key); grading compares them as sets. A question already checked
  // through /quiz-check is graded on that first check.
  if (quiz.length && csrf) {
    await postQuiz(
      quiz.map((q, i) => {
        const chosenId = answers[q.id];
        return { question_position: i, selected_choices: chosenId ? [chosenId] : [] };
      }),
    );
  }

  // Behavioural outcomes -> journey_outcomes (risk/positive) via /sim/track, as
  // the typed outcome post (kind sim_outcomes -> a sim_outcome training event).
  // event quiz_completed / debrief = the debrief signal of a 3D course.
  try {
    const res = st.score();
    const outcomes = st.outcomes.map((o) => ({
      type: o.type,
      beatId: o.beatId,
      eventId: o.eventId,
      evidence: o.evidence,
      at: o.at,
    }));
    await fetch(apiUrl("/sim/track"), {
      method: "POST",
      headers: hostPostHeaders(),
      credentials: "include",
      body: hostBody({
        token: host.token,
        kind: "sim_outcomes",
        event: quiz.length ? "quiz_completed" : "debrief",
        score: res,
        outcomes,
      }),
    });
  } catch {
    /* best-effort */
  }

  // No quiz: the empty submission, after the debrief post it depends on.
  if (!quiz.length && csrf) await postQuiz([]);
}

/** SCORM launcher only. The flag is on the export URL, so a normal course does nothing.
 *  `gradedCorrect` is the server-graded count of a hostGraded spec (no local keys). */
function reportScormLauncher(answers: Record<string, string>, gradedCorrect?: number): void {
  try {
    if (new URLSearchParams(window.location.search).get("cfCompletion") !== "1") return;
    const scn = useSim.getState().scenario;
    const quiz: QuizQuestion[] = scn?.beats.find((b) => b.type === "quiz")?.quiz ?? [];
    const message: Record<string, string | number | boolean> = {
      source: "cyberfrog-course",
      type: "complete",
      reachedEnd: true,
    };
    if (quiz.length) {
      const correct =
        gradedCorrect ?? quiz.filter((q) => q.options.find((o) => o.id === answers[q.id])?.correct).length;
      message.score = correct;
      message.max = quiz.length;
      message.percent = Math.round((correct / quiz.length) * 100);
    }
    const target = window.opener || (window.parent !== window ? window.parent : null);
    target?.postMessage(message, "*");
  } catch {
    /* the package window is optional */
  }
}

/**
 * Small line-icon set for the quiz (mirrors TimelineIcons.tsx's philosophy:
 * plain single-colour stroke icons, never colour emoji). Covers every
 * `option.icon` key used by an authored quiz today (see
 * ransomware-office.scenario.ts) plus a few generic extras + the chrome
 * furniture (chevron/bulb/clipboard/check/cross) the quiz shell itself needs.
 * An unknown/missing key falls back to a plain circle so a future author
 * adding a new icon name never renders a broken glyph.
 */
type GlyphName =
  | "alert"
  | "phone"
  | "mail"
  | "power"
  | "shield"
  | "link"
  | "lock"
  | "file"
  | "clock"
  | "x"
  | "check"
  | "folder"
  | "chrome"
  | "bulb"
  | "chevron"
  | "clipboard";

const GLYPH_PATHS: Record<GlyphName, ReactNode> = {
  alert: (
    <>
      <path d="M12 3.5 22 20.5H2Z" />
      <path d="M12 10v4.2" />
      <circle cx="12" cy="17.3" r=".6" fill="currentColor" stroke="none" />
    </>
  ),
  phone: (
    <path d="M6.6 3.5 9.4 9 6.8 11.4c1.1 2.2 3.2 4.3 5.4 5.4l2.4-2.4 5.5 2.8v3.1c0 1-.9 1.7-1.9 1.5C10.8 20.4 4.5 14.1 3.5 6.4c-.1-1 .6-1.9 1.6-1.9h1.5Z" />
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 6.5 12 13l8.5-6.5" />
    </>
  ),
  power: (
    <>
      <path d="M12 3v8" />
      <path d="M6.3 6.3a8 8 0 1 0 11.4 0" />
    </>
  ),
  shield: <path d="M12 3 20 6v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6Z" />,
  link: (
    <>
      <rect x="3" y="9" width="8" height="6" rx="3" />
      <rect x="13" y="9" width="8" height="6" rx="3" />
      <path d="M11 12h2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10.5" width="14" height="10" rx="1.5" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </>
  ),
  file: (
    <>
      <path d="M6 2.5h8.5L19 7v13.5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" />
      <path d="M14.5 2.5V7H19" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </>
  ),
  x: <path d="M6 6l12 12M18 6 6 18" />,
  check: <path d="M5 13l4 4L19 7" />,
  folder: <path d="M3 6h6l2 2h10v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6Z" />,
  chrome: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a13 13 0 0 1 0 18" />
      <path d="M12 3a13 13 0 0 0 0 18" />
    </>
  ),
  bulb: (
    <>
      <path d="M9.5 18h5M10.3 21h3.4" />
      <path d="M12 3a6 6 0 0 0-3.3 11c.5.35.8 1 .8 1.7v.3h5v-.3c0-.7.3-1.35.8-1.7A6 6 0 0 0 12 3Z" />
    </>
  ),
  chevron: <path d="M6 9l6 6 6-6" />,
  clipboard: (
    <>
      <rect x="6" y="4" width="12" height="17" rx="2" />
      <path d="M9 4V2.5h6V4" />
      <path d="M9 10.5h6M9 14.5h6" />
    </>
  ),
};

function Glyph({ name, className }: { name?: string; className?: string }) {
  const paths = GLYPH_PATHS[(name ?? "") as GlyphName];
  if (!paths) return null;
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths}
    </svg>
  );
}

/** Distinct colour per question (cycled), shared by the sidebar nav badge and
 *  the in-card header badge so the same question reads as the same colour in
 *  both places. Purely decorative -- never encodes correct/incorrect. */
const QUIZ_PALETTE = ["#2fd4a7", "#4aa8ff", "#f5a524", "#a78bfa", "#f472b6", "#5eead4"];

/**
 * End-of-scenario flow: a "Quick Quiz" FIRST (no score/correctness shown), then
 * — only after every question is answered — the results: pass/fail + risk,
 * quiz score, a per-question quiz review (your answer vs. the correct one +
 * rationale), and what the learner disclosed/resisted (from OUTCOME events,
 * never raw UI). The raw score + pass/fail is what a SCORM/xAPI wrapper would
 * report to the LMS.
 *
 * Quiz UI ("quiz3"): a left progress sidebar (per-question colour badge +
 * title + answered state + overall bar + a static tip) next to a
 * single-expanded accordion of question cards. Selecting an option never
 * reveals correct/wrong -- it just auto-advances to the next unanswered
 * question. Reopening an already-answered question (via the sidebar or its
 * own header) offers a manual "Next question" link since there's nothing
 * left to pick there.
 */
export function Debrief() {
  const scenario = useSim((s) => s.scenario);
  const meter = useSim((s) => s.riskMeter());
  const exposure = useSim((s) => s.exposureMap());
  const evidence = useSim((s) => s.evidence());
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // A course without quiz questions opens on its results, so reaching the debrief is what posts the debrief signal
  // (and the empty /quiz) to the host; there is no empty "Quick quiz" page to click through.
  const [phase, setPhase] = useState<"quiz" | "results">(() =>
    scenario && !(scenario.beats.find((b) => b.type === "quiz")?.quiz ?? []).length ? "results" : "quiz",
  );
  const reported = useRef(false);
  // hostGraded only: the server's verdict per question id (null = check failed).
  const [checks, setChecks] = useState<Record<string, HostCheck | null>>({});
  const hostGraded = isHostGraded(scenario);
  // hostGraded: the server verdict; when the check failed, the local key if this beat kept one (a game bank such as
  // Turkey Hunt keeps its keys), else wrong. Without hostGraded the local key as before.
  const isCorrect = (q: QuizQuestion): boolean => {
    const local = q.options.find((o) => o.id === answers[q.id])?.correct;
    if (!hostGraded) return !!local;
    const verdict = checks[q.id];
    return verdict ? verdict.correct : !!local;
  };

  const quiz: QuizQuestion[] = scenario?.beats.find((b) => b.type === "quiz")?.quiz ?? [];
  const [activeQ, setActiveQ] = useState<string | null>(() => quiz[0]?.id ?? null);
  // Answer order is shuffled once per attempt (a reload draws a new seed). Display order only: option ids, the grading
  // and the POST to the host are unchanged.
  const [shuffleSeed] = useState(newShuffleSeed);
  const orderedOptions = useMemo(() => shuffleOptionSets(quiz.map((q) => q.options), shuffleSeed), [quiz, shuffleSeed]);

  // When the learner reaches results, report quiz + outcomes to the host once.
  useEffect(() => {
    if (phase !== "results" || reported.current) return;
    reported.current = true;
    void reportToHost(answers);
    reportScormLauncher(answers, hostGraded ? quiz.filter(isCorrect).length : undefined);
  }, [phase, answers]);

  if (!scenario) return null;

  // hostGraded: "See my results" waits until every answer has its server verdict.
  const allAnswered =
    quiz.length === 0 || quiz.every((q) => answers[q.id] && (!hostGraded || checks[q.id] !== undefined));
  const answeredCount = quiz.filter((q) => answers[q.id]).length;
  const quizCorrect = quiz.filter(isCorrect).length;

  const nextUnansweredAfter = (afterId: string, snapshot: Record<string, string>) => {
    const idx = quiz.findIndex((x) => x.id === afterId);
    return (
      quiz.find((x, i) => i > idx && !snapshot[x.id]) ?? quiz.find((x) => !snapshot[x.id]) ?? null
    );
  };

  const selectOption = (q: QuizQuestion, optId: string) => {
    if (answers[q.id]) return; // already answered -- don't let a re-click change the record
    const next = { ...answers, [q.id]: optId };
    setAnswers(next);
    if (hostGraded) {
      // Server grading (no feedback shown before the results, as with local keys).
      const position = quiz.findIndex((x) => x.id === q.id);
      void checkQuizAnswer(position, optId).then((verdict) => setChecks((c) => ({ ...c, [q.id]: verdict })));
    }
    const target = nextUnansweredAfter(q.id, next);
    window.setTimeout(() => setActiveQ(target ? target.id : null), 380);
  };

  // ---------- QUIZ phase ----------
  if (phase === "quiz") {
    return (
      <div className="debrief quiz3-debrief">
        <div className="quiz3">
          <aside className="quiz3-side">
            <div>
              <span className="quiz3-side-label">{tx("Quick quiz")}</span>
              <div className="quiz3-side-count">
                {txr("{answeredCount} of {length} answered", { answeredCount, length: quiz.length })}
              </div>
              <div className="quiz3-bar">
                <span style={{ width: `${quiz.length ? (answeredCount / quiz.length) * 100 : 0}%` }} />
              </div>
            </div>
            <nav className="quiz3-nav">
              {quiz.map((q, qi) => {
                const color = QUIZ_PALETTE[qi % QUIZ_PALETTE.length];
                const done = !!answers[q.id];
                return (
                  <button
                    key={q.id}
                    type="button"
                    className={`quiz3-nav-item ${activeQ === q.id ? "active" : ""} ${done ? "done" : ""}`}
                    onClick={() => setActiveQ(q.id)}
                  >
                    <span className="quiz3-nav-badge" style={{ background: color }}>
                      {done ? <Glyph name="check" /> : qi + 1}
                    </span>
                    <span className="quiz3-nav-txt">
                      <span className="quiz3-nav-title">{q.title ?? tx("Question {v}", { v: qi + 1 })}</span>
                      <span className="quiz3-nav-status">{done ? tx("Answered") : tx("Not answered")}</span>
                    </span>
                  </button>
                );
              })}
            </nav>
            <div className="quiz3-tip">
              <span className="quiz3-tip-ico">
                <Glyph name="bulb" />
              </span>
              <div>
                <strong>{tx("Tip")}</strong>
                <p>{tx("There's no scoring pressure here — take your time on each question before moving on.")}</p>
              </div>
            </div>
          </aside>

          <div className="quiz3-main">
            <div className="quiz3-head">
              <span className="quiz3-kicker">
                <Glyph name="clipboard" /> {tx("QUICK QUIZ")}
              </span>
              <h2>{tx("Quick Quiz")}</h2>
              <p className="quiz3-intro">
                {tx("A few quick questions on what just happened. Answer them all, then see how you did.")}
              </p>
            </div>

            <div className="quiz3-list">
              {quiz.map((q, qi) => {
                const color = QUIZ_PALETTE[qi % QUIZ_PALETTE.length];
                const open = activeQ === q.id;
                const chosen = answers[q.id];
                const target = chosen ? nextUnansweredAfter(q.id, answers) : null;
                return (
                  <div key={q.id} className={`quiz3-q ${open ? "open" : "collapsed"}`}>
                    <button
                      type="button"
                      className="quiz3-q-head"
                      onClick={() => setActiveQ(open ? null : q.id)}
                    >
                      <span className="quiz3-q-num" style={{ background: color }}>
                        {qi + 1}
                      </span>
                      <span className="quiz3-q-prompt">{q.prompt}</span>
                      <span className={`quiz3-q-chev ${open ? "up" : ""}`}>
                        <Glyph name="chevron" />
                      </span>
                    </button>
                    {open && (
                      <div className="quiz3-q-body">
                        <div className="quiz3-divider" />
                        <div className="quiz3-opts">
                          {orderedOptions[qi].map((opt) => (
                            <button
                              key={opt.id}
                              type="button"
                              className={`quiz3-opt ${chosen === opt.id ? "selected" : ""}`}
                              disabled={!!chosen}
                              onClick={() => selectOption(q, opt.id)}
                            >
                              <span className={`quiz3-radio ${chosen === opt.id ? "on" : ""}`}>
                                {chosen === opt.id && <span style={{ background: color }} />}
                              </span>
                              <span className="quiz3-opt-label">{opt.label}</span>
                              {opt.icon && (
                                <span className="quiz3-opt-glyph">
                                  <Glyph name={opt.icon} className="quiz3-opt-ico" />
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                        {q.hint && (
                          <div className="quiz3-hint">
                            <Glyph name="bulb" />
                            <span>
                              <strong>{tx("Think:")}</strong> {q.hint}
                            </span>
                          </div>
                        )}
                        {chosen && target && (
                          <div className="quiz3-q-next">
                            <button
                              type="button"
                              className="quiz3-next-link"
                              onClick={() => setActiveQ(target.id)}
                            >
                              {tx("Next question")} {"\u2192"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="quiz3-spacer" />
          </div>

          <div className="quiz3-foot">
            <span className="quiz3-foot-status">
              <Glyph name="clipboard" />
              {txr("{answeredCount}/{length} answered", { answeredCount, length: quiz.length })}
            </span>
            <button className="btn quiz3-see" disabled={!allAnswered} onClick={() => setPhase("results")}>
              {tx("See my results")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------- RESULTS phase ----------
  // The pass/fail verdict is judged on the QUIZ ONLY. Several beats in this
  // scenario deliberately force the learner down a "bad" branch to SHOW the
  // consequences of a risky choice (that's the whole point of a simulation) —
  // it would be unfair to then grade the learner on choices the scenario
  // itself required. Behavioural walkthrough detail is still available below
  // as an optional, non-judgmental "review" card, never folded into the score.
  const quizPassed = quiz.length === 0 || quizCorrect / quiz.length > 0.5;
  // hostGraded: the review shows the verdict on the learner's own answer (isCorrect) and the
  // rationale quiz-check returned (the right option of a wrong answer stays unknown unless the beat kept its keys).
  const reviewQuiz: QuizQuestion[] = hostGraded
    ? quiz.map((q) => {
        const ok = isCorrect(q);
        return {
          ...q,
          rationale: checks[q.id]?.rationale ?? q.rationale,
          options: q.options.map((o) => (o.id === answers[q.id] ? { ...o, correct: ok } : o)),
        };
      })
    : quiz;

  return (
    <ResultsCompact
      title={scenario.title}
      quiz={reviewQuiz}
      answers={answers}
      passed={quizPassed}
      correct={quizCorrect}
      dims={exposure.enabled ? exposure.dims : null}
      evidence={exposure.enabled ? evidence : []}
      choices={meter.enabled ? { count: meter.items.length, body: <RiskMeterList items={meter.items} /> } : null}
    />
  );
}
